import { ref, onMounted, onUnmounted } from "vue";

export interface ColumnResizeOptions {
  minWidth?: number;
  maxWidth?: number;
  onResize?: (fieldKey: string, width: number) => void;
  onResizeEnd?: (fieldKey: string, width: number) => void;
}

export function useColumnResize(options: ColumnResizeOptions = {}) {
  const { minWidth = 50, maxWidth = 500, onResize, onResizeEnd } = options;

  const isResizing = ref(false);
  const currentField = ref<string | null>(null);
  const startX = ref(0);
  const startWidth = ref(0);
  const currentWidth = ref(0);

  /**
   * Начало изменения ширины столбца
   */
  const startResize = (
    event: MouseEvent,
    fieldKey: string,
    initialWidth: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    isResizing.value = true;
    currentField.value = fieldKey;
    startX.value = event.clientX;
    startWidth.value = initialWidth;
    currentWidth.value = initialWidth;

    // Добавляем слушатели событий на document
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Добавляем класс для отключения выделения текста
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  /**
   * Обработка движения мыши
   */
  const handleMouseMove = (event: MouseEvent) => {
    if (!isResizing.value || !currentField.value) return;

    const deltaX = event.clientX - startX.value;
    let newWidth = startWidth.value + deltaX;

    // Ограничиваем ширину минимальным и максимальным значениями
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    currentWidth.value = newWidth;

    if (onResize) {
      onResize(currentField.value, newWidth);
    }
  };

  /**
   * Обработка отпускания кнопки мыши
   */
  const handleMouseUp = () => {
    if (isResizing.value && currentField.value && onResizeEnd) {
      onResizeEnd(currentField.value, currentWidth.value);
    }

    // Сбрасываем состояние
    isResizing.value = false;
    currentField.value = null;

    // Удаляем слушатели событий
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    // Восстанавливаем стили body
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };

  /**
   * Получение текущей ширины столбца
   */
  const getCurrentWidth = () => currentWidth.value;

  /**
   * Проверка, является ли поле текущим изменяемым
   */
  const isCurrentField = (fieldKey: string) => currentField.value === fieldKey;

  return {
    isResizing,
    currentField,
    currentWidth,
    startResize,
    getCurrentWidth,
    isCurrentField,
  };
}

export type ColumnResize = ReturnType<typeof useColumnResize>;
